import React, { useEffect, useMemo, useState } from "react";
import { api, formatMoney, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  ClipboardCheck, FileDown, Search, ArrowDownCircle, ArrowUpCircle,
  Boxes, RefreshCcw,
} from "lucide-react";

export default function AdjustmentLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(todayISO());
  const [godown, setGodown] = useState("");
  const [brand, setBrand] = useState("");
  const [size, setSize] = useState("");
  const [q, setQ] = useState("");
  const [godowns, setGodowns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/godowns"), api.get("/brands"), api.get("/sizes")])
      .then(([g, b, s]) => { setGodowns(g.data); setBrands(b.data); setSizes(s.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { type: "adjustment", limit: 5000 };
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get("/transactions", { params });
      // Flatten each adjustment txn into one row per item
      const flat = [];
      for (const t of data || []) {
        for (const it of (t.items || [])) {
          flat.push({
            id: `${t.id}-${it.brand}-${it.size}`,
            txn_id: t.id,
            date: t.date,
            godown: t.godown || t.to_godown || t.from_godown || "",
            challan_no: t.challan_no || "",
            brand: it.brand || "",
            size: it.size || "",
            filled_qty: Number(it.filled_qty || 0),
            mt_qty: Number(it.mt_qty || 0),
            notes: t.notes || "",
            created_by_email: t.created_by_email || "",
            created_at: t.created_at || "",
          });
        }
      }
      flat.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setRows(flat);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) =>
      (!godown || r.godown === godown) &&
      (!brand || r.brand === brand) &&
      (!size || r.size === size) &&
      (!s || r.notes.toLowerCase().includes(s) || r.challan_no.toLowerCase().includes(s) || r.brand.toLowerCase().includes(s))
    );
  }, [rows, q, godown, brand, size]);

  const totals = useMemo(() => ({
    count: filtered.length,
    txns: new Set(filtered.map((r) => r.txn_id)).size,
    filled_in:  filtered.reduce((s, r) => s + (r.filled_qty > 0 ? r.filled_qty : 0), 0),
    filled_out: filtered.reduce((s, r) => s + (r.filled_qty < 0 ? -r.filled_qty : 0), 0),
    mt_in:  filtered.reduce((s, r) => s + (r.mt_qty > 0 ? r.mt_qty : 0), 0),
    mt_out: filtered.reduce((s, r) => s + (r.mt_qty < 0 ? -r.mt_qty : 0), 0),
  }), [filtered]);

  const exportCsv = () => {
    const lines = [["Date", "Godown", "Brand", "Size", "Filled Δ", "MT Δ", "Challan", "Notes", "By"]];
    filtered.forEach((r) => lines.push([
      r.date, r.godown, r.brand, r.size,
      String(r.filled_qty), String(r.mt_qty),
      r.challan_no, (r.notes || "").replace(/\n/g, " "), r.created_by_email,
    ]));
    const csv = lines.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `adjustments_${from}_to_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="adjustment-log-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Adjustment Log</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Every stock adjustment (from tally mismatches + manual entries)
        </span>
        <button onClick={load} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black" data-testid="adj-refresh">
          <RefreshCcw size={13} /> Refresh
        </button>
        <button onClick={exportCsv} className="h-9 px-3 bg-black text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5" data-testid="adj-export"><FileDown size={13} /> CSV</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Rows</div>
          <div className="data-num text-3xl mt-1" data-testid="adj-count">{totals.count}</div>
          <div className="text-xs text-gray-500 mt-1">{totals.txns} adjustment entries</div>
        </div>
        <div className="card-flat p-5 bg-[#DCFCE7] border-[#BBF7D0]">
          <div className="small-label text-[#15803D] flex items-center gap-1.5"><ArrowDownCircle size={12} /> Filled +</div>
          <div className="data-num text-3xl mt-1 text-[#15803D]" data-testid="adj-filled-in">{totals.filled_in}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><ArrowUpCircle size={12} /> Filled −</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="adj-filled-out">{totals.filled_out}</div>
        </div>
        <div className="card-flat p-5 bg-[#DCFCE7] border-[#BBF7D0]">
          <div className="small-label text-[#15803D] flex items-center gap-1.5"><ArrowDownCircle size={12} /> MT +</div>
          <div className="data-num text-3xl mt-1 text-[#15803D]" data-testid="adj-mt-in">{totals.mt_in}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><ArrowUpCircle size={12} /> MT −</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="adj-mt-out">{totals.mt_out}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <input type="date" className="input-lg h-9" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="adj-from" />
        <span>→</span>
        <input type="date" className="input-lg h-9" value={to} onChange={(e) => setTo(e.target.value)} data-testid="adj-to" />
        <select className="input-lg h-9 w-40 ml-2" value={godown} onChange={(e) => setGodown(e.target.value)} data-testid="adj-godown">
          <option value="">All godowns</option>
          {godowns.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
        </select>
        <select className="input-lg h-9 w-36" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="adj-brand">
          <option value="">All brands</option>
          {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
        </select>
        <select className="input-lg h-9 w-28" value={size} onChange={(e) => setSize(e.target.value)} data-testid="adj-size">
          <option value="">All sizes</option>
          {sizes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input-lg h-9 w-60 pl-8" placeholder="Notes / challan / brand…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="adj-search" />
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Godown</th>
              <th className="text-left">Brand</th>
              <th className="text-left">Size</th>
              <th className="num">Filled Δ</th>
              <th className="num">MT Δ</th>
              <th className="text-left">Challan</th>
              <th className="text-left">Notes</th>
              <th className="text-left">By</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="py-8 text-center text-gray-400 text-sm">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="9" className="py-8 text-center text-gray-400 text-sm">
                <Boxes className="mx-auto text-gray-300 mb-2" size={28} />
                No adjustments match this filter.
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.id} data-testid={`adj-row-${r.id}`}>
                <td className="data-num text-xs whitespace-nowrap">{r.date}</td>
                <td className="text-sm">{r.godown}</td>
                <td className="text-sm font-semibold">{r.brand}</td>
                <td className="text-sm">{r.size}</td>
                <td className={`num data-num font-bold ${r.filled_qty > 0 ? "text-[#15803D]" : r.filled_qty < 0 ? "text-[#B91C1C]" : "text-gray-400"}`}>
                  {r.filled_qty > 0 ? `+${r.filled_qty}` : r.filled_qty || ""}
                </td>
                <td className={`num data-num font-bold ${r.mt_qty > 0 ? "text-[#15803D]" : r.mt_qty < 0 ? "text-[#B91C1C]" : "text-gray-400"}`}>
                  {r.mt_qty > 0 ? `+${r.mt_qty}` : r.mt_qty || ""}
                </td>
                <td className="data-num text-xs">{r.challan_no || <span className="text-gray-400">—</span>}</td>
                <td className="text-xs text-gray-600 max-w-[280px] truncate" title={r.notes}>{r.notes || <span className="text-gray-400">—</span>}</td>
                <td className="text-xs text-gray-500">{r.created_by_email}</td>
              </tr>
            ))}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot>
              <tr className="font-bold bg-gray-50">
                <td colSpan="4" className="text-right pr-3">Totals · {filtered.length} rows / {totals.txns} entries</td>
                <td className="num">
                  <span className="text-[#15803D]">+{totals.filled_in}</span>
                  <span className="text-gray-300 mx-1">/</span>
                  <span className="text-[#B91C1C]">-{totals.filled_out}</span>
                </td>
                <td className="num">
                  <span className="text-[#15803D]">+{totals.mt_in}</span>
                  <span className="text-gray-300 mx-1">/</span>
                  <span className="text-[#B91C1C]">-{totals.mt_out}</span>
                </td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="text-xs text-gray-500">
        Positive numbers mean cylinders were added to the godown, negative means removed. Most adjustments are auto-created from daily tally mismatches; look for the tally-date in the Notes column.
      </p>
    </div>
  );
}
